/**
 * CronManager Component
 * UI za upravljanje cron jobovima za feed import
 */

import { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tooltip,
  CircularProgress,
  Switch,
  FormControlLabel,
} from '@mui/material';
import {
  Schedule as ScheduleIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  PlayArrow as RunIcon,
  Pause as PauseIcon,
  History as HistoryIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { supabase } from '../../../lib/supabase';
import { adminApi } from '../../../lib/adminApi';

interface CronJob {
  jobid: number;
  jobname: string;
  schedule: string;
  command: string;
  active: boolean;
  nodename: string;
}

interface JobRunDetail {
  runid: number;
  jobid: number;
  job_pid: number;
  status: string;
  return_message: string;
  start_time: string;
  end_time: string;
}

interface Supplier {
  id: number;
  name: string;
  feed_url: string;
  enabled: boolean;
}

// Predefined schedule options
const SCHEDULE_PRESETS = [
  { label: 'Svaki sat', value: '0 * * * *' },
  { label: 'Svaka 2 sata', value: '0 */2 * * *' },
  { label: 'Svaka 4 sata', value: '0 */4 * * *' },
  { label: 'Svaka 6 sati', value: '0 */6 * * *' },
  { label: 'Svaka 12 sati', value: '0 */12 * * *' },
  { label: 'Svaki dan u 6:00', value: '0 6 * * *' },
  { label: 'Svaki dan u 8:00', value: '0 8 * * *' },
  { label: 'Svaki dan u 20:00', value: '0 20 * * *' },
  { label: 'Radnim danima u 6:00', value: '0 6 * * 1-5' },
  { label: 'Ponedjeljkom u 6:00', value: '0 6 * * 1' },
  { label: 'Custom', value: 'custom' },
];

export function CronManager() {
  const [jobs, setJobs] = useState<CronJob[]>([]);
  const [history, setHistory] = useState<JobRunDetail[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [loading, setLoading] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);

  // New job form
  const [newJobSupplier, setNewJobSupplier] = useState<string>('');
  const [newJobSchedulePreset, setNewJobSchedulePreset] = useState<string>('0 6 * * *');
  const [newJobCustomSchedule, setNewJobCustomSchedule] = useState<string>('');

  /**
   * Load cron jobs from database
   */
  const loadJobs = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await adminApi.cron.rpc('get_cron_jobs');
      if (error) throw error;
      setJobs(data || []);
    } catch (error: any) {
      console.error('Error loading cron jobs:', error);
      // Fallback: direct query (requires proper permissions)
      try {
        const { data } = await supabase
          .from('cron.job' as any)
          .select('*');
        setJobs(data || []);
      } catch {
        toast.error('Ne mogu učitati cron jobove');
      }
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * Load suppliers for dropdown
   */
  const loadSuppliers = useCallback(async () => {
    try {
      const all = await adminApi.suppliers.list();
      // Only keep the fields this component needs
      setSuppliers(
        (all || []).map((s: any) => ({
          id: s.id,
          name: s.name,
          feed_url: s.feed_url,
          enabled: s.enabled,
        }))
      );
    } catch (err) {
      console.error('[CronManager] loadSuppliers failed:', err);
      setSuppliers([]);
    }
  }, []);

  /**
   * Load job run history
   */
  const loadHistory = useCallback(async (jobId?: number) => {
    try {
      const { data, error } = await adminApi.cron.rpc('get_cron_job_history', {
        p_job_id: jobId || null,
        p_limit: 50
      });
      if (error) throw error;
      setHistory(data || []);
    } catch (error) {
      console.error('Error loading history:', error);
    }
  }, []);

  useEffect(() => {
    loadJobs();
    loadSuppliers();
  }, [loadJobs, loadSuppliers]);

  /**
   * Create new cron job
   */
  const handleCreateJob = async () => {
    if (!newJobSupplier) {
      toast.error('Odaberi dobavljača');
      return;
    }

    const schedule = newJobSchedulePreset === 'custom'
      ? newJobCustomSchedule
      : newJobSchedulePreset;

    if (!schedule) {
      toast.error('Unesi schedule');
      return;
    }

    setLoading(true);
    try {
      const jobName = `${newJobSupplier.toLowerCase()}-import`;

      const { error } = await adminApi.cron.rpc('create_feed_import_cron', {
        p_job_name: jobName,
        p_schedule: schedule,
        p_supplier_name: newJobSupplier
      });

      if (error) throw error;

      toast.success(`Cron job "${jobName}" kreiran`);
      setDialogOpen(false);
      setNewJobSupplier('');
      setNewJobSchedulePreset('0 6 * * *');
      setNewJobCustomSchedule('');
      loadJobs();
    } catch (error: any) {
      toast.error('Greška: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Toggle job active/inactive
   */
  const handleToggleJob = async (jobId: number, currentActive: boolean) => {
    try {
      const { error } = await adminApi.cron.rpc('toggle_cron_job', {
        p_job_id: jobId,
        p_active: !currentActive
      });

      if (error) throw error;
      toast.success(currentActive ? 'Job pauziran' : 'Job aktiviran');
      loadJobs();
    } catch (error: any) {
      toast.error('Greška: ' + error.message);
    }
  };

  /**
   * Delete cron job
   */
  const handleDeleteJob = async (jobName: string) => {
    if (!confirm(`Obrisati job "${jobName}"?`)) return;

    try {
      const { error } = await adminApi.cron.rpc('delete_cron_job', {
        p_job_name: jobName
      });

      if (error) throw error;
      toast.success('Job obrisan');
      loadJobs();
    } catch (error: any) {
      toast.error('Greška: ' + error.message);
    }
  };

  /**
   * Run job manually using supabase.functions.invoke for proper auth
   */
  const handleRunNow = async (jobName: string, command?: string) => {
    try {
      // Check if it's a feed import job or other type
      if (jobName.endsWith('-import') || jobName.startsWith('feed-import-')) {
        // Feed import job - extract supplier name
        let rawName: string;
        if (jobName.startsWith('feed-import-')) {
          rawName = jobName.replace('feed-import-', '');
        } else {
          rawName = jobName.replace('-import', '');
        }
        const supplierName = rawName.toUpperCase();
        toast.info(`Pokrećem import za ${supplierName}...`);

        // Use supabase.functions.invoke which handles auth automatically
        const { data, error } = await supabase.functions.invoke('feed-import', {
          body: { supplier_name: supplierName },
        });

        if (error) {
          toast.error('Greška: ' + error.message);
          return;
        }

        if (data?.success) {
          const result = data.results?.[0];
          toast.success(`Import završen: +${result?.created || 0} ~${result?.updated || 0} -${result?.deleted || 0}`);
        } else {
          toast.error('Greška: ' + (data?.error || 'Unknown error'));
        }
      } else if (jobName === 'daily-backup') {
        // Backup job - call daily-backup edge function
        toast.info('Pokrećem backup...');

        const { data, error } = await supabase.functions.invoke('daily-backup');

        if (error) {
          toast.error('Greška: ' + error.message);
          return;
        }

        if (data?.success) {
          toast.success(data.message || 'Backup završen');
        } else {
          toast.error('Greška: ' + (data?.error || 'Unknown error'));
        }
      } else {
        // Unknown job type
        toast.warning(`Nepoznat tip joba: ${jobName}`);
      }
    } catch (error: any) {
      toast.error('Greška: ' + error.message);
    }
  };

  /**
   * Show history for job
   */
  const handleShowHistory = async (jobId: number) => {
    setSelectedJobId(jobId);
    await loadHistory(jobId);
    setHistoryDialogOpen(true);
  };

  /**
   * Parse schedule to human readable
   */
  const parseSchedule = (cron: string): string => {
    const preset = SCHEDULE_PRESETS.find(p => p.value === cron);
    if (preset && preset.value !== 'custom') return preset.label;

    // Basic parsing
    const parts = cron.split(' ');
    if (parts.length !== 5) return cron;

    const [min, hour, day, month, dow] = parts;

    if (hour.startsWith('*/')) {
      return `Svaka ${hour.slice(2)} sata`;
    }
    if (min === '0' && !hour.includes('*') && day === '*' && month === '*') {
      if (dow === '*') return `Svaki dan u ${hour}:00`;
      if (dow === '1-5') return `Radnim danima u ${hour}:00`;
    }

    return cron;
  };

  return (
    <Box>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ScheduleIcon /> Cron Jobovi
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            size="small"
            startIcon={<RefreshIcon />}
            onClick={loadJobs}
            disabled={loading}
          >
            Osvježi
          </Button>
          <Button
            variant="contained"
            size="small"
            startIcon={<AddIcon />}
            onClick={() => setDialogOpen(true)}
          >
            Novi Job
          </Button>
        </Box>
      </Box>

      {/* Jobs Table */}
      <TableContainer component={Paper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Status</TableCell>
              <TableCell>Naziv</TableCell>
              <TableCell>Schedule</TableCell>
              <TableCell>Opis</TableCell>
              <TableCell align="right">Akcije</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <CircularProgress size={24} />
                </TableCell>
              </TableRow>
            ) : jobs.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography variant="body2" color="text.secondary">
                    Nema konfiguriranih cron jobova
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              jobs.map(job => (
                <TableRow key={job.jobid}>
                  <TableCell>
                    <Chip
                      size="small"
                      label={job.active ? 'Aktivan' : 'Pauziran'}
                      color={job.active ? 'success' : 'default'}
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>
                      {job.jobname}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Tooltip title={job.schedule}>
                      <Typography variant="body2">
                        {parseSchedule(job.schedule)}
                      </Typography>
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="text.secondary">
                      {job.jobname === 'daily-backup'
                        ? 'Backup baze'
                        : job.command.includes('supplier_name=')
                          ? `Import: ${job.command.match(/supplier_name=(\w+)/)?.[1] || 'ALL'}`
                          : job.jobname.endsWith('-import')
                            ? `Import: ${job.jobname.replace('-import', '').toUpperCase()}`
                            : job.jobname
                      }
                    </Typography>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Pokreni odmah">
                      <IconButton size="small" onClick={() => handleRunNow(job.jobname)}>
                        <RunIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={job.active ? 'Pauziraj' : 'Aktiviraj'}>
                      <IconButton
                        size="small"
                        onClick={() => handleToggleJob(job.jobid, job.active)}
                      >
                        <PauseIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Historija">
                      <IconButton size="small" onClick={() => handleShowHistory(job.jobid)}>
                        <HistoryIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Obriši">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => handleDeleteJob(job.jobname)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Info */}
      <Alert severity="info" sx={{ mt: 2 }}>
        <Typography variant="body2">
          Cron jobovi automatski pokreću import feeda po definiranom rasporedu.
          Svi importi koriste istu logiku kao ručni import iz Feed Managera.
        </Typography>
      </Alert>

      {/* Create Job Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Novi Cron Job</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Dobavljač</InputLabel>
              <Select
                value={newJobSupplier}
                onChange={(e) => setNewJobSupplier(e.target.value)}
                label="Dobavljač"
              >
                <MenuItem value="">-- Odaberi --</MenuItem>
                {suppliers.map(s => (
                  <MenuItem key={s.id} value={s.name}>
                    {s.name} {!s.enabled && '(disabled)'}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Raspored</InputLabel>
              <Select
                value={newJobSchedulePreset}
                onChange={(e) => setNewJobSchedulePreset(e.target.value)}
                label="Raspored"
              >
                {SCHEDULE_PRESETS.map(preset => (
                  <MenuItem key={preset.value} value={preset.value}>
                    {preset.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {newJobSchedulePreset === 'custom' && (
              <TextField
                fullWidth
                label="Custom Cron Expression"
                value={newJobCustomSchedule}
                onChange={(e) => setNewJobCustomSchedule(e.target.value)}
                placeholder="0 6 * * *"
                helperText="Format: minute hour day month weekday"
              />
            )}

            <Alert severity="info" sx={{ fontSize: '0.8rem' }}>
              <strong>Primjeri:</strong><br />
              <code>0 6 * * *</code> = Svaki dan u 6:00<br />
              <code>0 */4 * * *</code> = Svaka 4 sata<br />
              <code>0 8,20 * * *</code> = U 8:00 i 20:00<br />
              <code>0 6 * * 1-5</code> = Radnim danima u 6:00
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Odustani</Button>
          <Button
            variant="contained"
            onClick={handleCreateJob}
            disabled={loading || !newJobSupplier}
          >
            Kreiraj
          </Button>
        </DialogActions>
      </Dialog>

      {/* History Dialog */}
      <Dialog
        open={historyDialogOpen}
        onClose={() => setHistoryDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Historija Izvršavanja</DialogTitle>
        <DialogContent>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Vrijeme</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Trajanje</TableCell>
                  <TableCell>Poruka</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {history.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center">
                      Nema historije
                    </TableCell>
                  </TableRow>
                ) : (
                  history.map(run => (
                    <TableRow key={run.runid}>
                      <TableCell>
                        {new Date(run.start_time).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Chip
                          size="small"
                          label={run.status}
                          color={run.status === 'succeeded' ? 'success' : 'error'}
                        />
                      </TableCell>
                      <TableCell>
                        {run.end_time
                          ? `${Math.round((new Date(run.end_time).getTime() - new Date(run.start_time).getTime()) / 1000)}s`
                          : '-'
                        }
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" sx={{ maxWidth: 300 }} noWrap>
                          {run.return_message || '-'}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryDialogOpen(false)}>Zatvori</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
